import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, FormGroup, InputGroup, Callout, Elevation } from "@blueprintjs/core";
import { login, setNewPassword } from "../lib/auth";
import { useAuth } from "../lib/AuthContext";

type Phase = "login" | "new-password";

export function Login() {
  const navigate = useNavigate();
  const { setAuth } = useAuth();

  const [phase, setPhase] = useState<Phase>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [session, setSession] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.challenge === "NEW_PASSWORD_REQUIRED") {
        setPhase("new-password");
        setSession(result.session || "");
        setPassword("");
      } else if (result.tokens) {
        await setAuth(result.tokens);
        navigate("/dash");
      }
    } catch (err: any) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (newPwd !== confirmPwd) {
      setError("Passwords do not match");
      return;
    }
    if (newPwd.length < 10) {
      setError("Password must be at least 10 characters");
      return;
    }

    setLoading(true);
    try {
      const tokens = await setNewPassword(email, newPwd, session);
      await setAuth(tokens);
      navigate("/dash");
    } catch (err: any) {
      setError(err.message || "Password change failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-brand">
          <div className="sidebar-logo-icon" style={{ width: 40, height: 40, fontSize: 20 }}>C</div>
          <span className="login-brand-text">Criterion</span>
        </div>

        <Card elevation={Elevation.TWO} className="login-card">
          {phase === "login" ? (
            <>
              <h3 className="login-title">Sign in to your account</h3>
              <p className="login-subtitle">Enter your credentials to continue</p>

              {error && (
                <Callout intent="danger" icon="error" style={{ marginBottom: 20 }}>
                  {error}
                </Callout>
              )}

              <form onSubmit={handleLogin}>
                <FormGroup label="Email address" labelFor="email">
                  <InputGroup
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    leftIcon="envelope"
                    large
                    required
                    autoFocus
                  />
                </FormGroup>

                <FormGroup label="Password" labelFor="password">
                  <InputGroup
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    leftIcon="lock"
                    large
                    required
                  />
                </FormGroup>

                <Button
                  type="submit"
                  intent="primary"
                  large
                  fill
                  loading={loading}
                  text="Sign In"
                  style={{ marginTop: 8 }}
                />
              </form>
            </>
          ) : (
            <>
              <h3 className="login-title">Set your new password</h3>
              <p className="login-subtitle">
                Your account requires a password change. Choose a strong password.
              </p>

              {error && (
                <Callout intent="danger" icon="error" style={{ marginBottom: 20 }}>
                  {error}
                </Callout>
              )}

              <form onSubmit={handleSetPassword}>
                <FormGroup label="New Password" labelFor="new-password" helperText="Min 10 chars, upper + lower + number + symbol">
                  <InputGroup
                    id="new-password"
                    type="password"
                    placeholder="New password"
                    value={newPwd}
                    onChange={(e) => setNewPwd(e.target.value)}
                    leftIcon="lock"
                    large
                    required
                    autoFocus
                  />
                </FormGroup>

                <FormGroup label="Confirm Password" labelFor="confirm-password">
                  <InputGroup
                    id="confirm-password"
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmPwd}
                    onChange={(e) => setConfirmPwd(e.target.value)}
                    leftIcon="lock"
                    large
                    required
                  />
                </FormGroup>

                <Button
                  type="submit"
                  intent="primary"
                  large
                  fill
                  loading={loading}
                  text="Set Password & Sign In"
                  style={{ marginTop: 8 }}
                />
              </form>
            </>
          )}
        </Card>

        <p className="login-footer-text">
          Accounts are created by your administrator. Contact your admin if you need access.
        </p>
      </div>
    </div>
  );
}
